import type { QueryClient } from '@tanstack/react-query';
import { Button } from 'flowbite-react';
import {
  HiArrowPath,
  HiCpuChip,
  HiDevicePhoneMobile,
  HiVideoCamera,
} from 'react-icons/hi2';
import {
  Link,
  useParams,
  useSearchParams,
  type LoaderFunctionArgs,
} from 'react-router-dom';

import { ContentLoader } from '@/components/loaders/content-loader';
import {
  getCourtQueryOptions,
  useCourt,
} from '@/features/courts/api/get-court';
import { ClockDisplay } from '@/features/courts/components/sports/clock/clock-display';
import { DodgeballDisplay } from '@/features/courts/components/sports/dodgeball/dodgeball-display';
import { SetsDisplay } from '@/features/courts/components/sports/sets/sets-display';
import type { ScoreboardType } from '@/types/api';

const SPORT_DISPLAYS: Partial<Record<ScoreboardType, typeof DodgeballDisplay>> =
  {
    clock: ClockDisplay,
    dodgeball: DodgeballDisplay,
    sets: SetsDisplay,
  };

export const courtLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const courtId = params.courtId as string;

    const query = getCourtQueryOptions(courtId);

    return (
      queryClient.getQueryData(query.queryKey) ??
      (await queryClient.fetchQuery(query))
    );
  };

export const CourtPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const courtId = params.courtId as string;
  const courtQuery = useCourt({ courtId });

  const setInverse = () => {
    if (searchParams.has('inverted')) {
      searchParams.delete('inverted');
    } else {
      searchParams.set('inverted', 'true');
    }
    setSearchParams(searchParams);
  };

  if (courtQuery.isLoading) {
    return <ContentLoader />;
  }

  if (!courtQuery.data) {
    return null;
  }

  const { court } = courtQuery.data;

  const SportSpecificDisplay = court.active_match
    ? SPORT_DISPLAYS[court.active_match?.scoreboard]
    : null;

  return (
    <>
      <div className="flex h-screen w-screen items-center justify-center bg-white landscape:hidden">
        <div className="font-display text-xl">
          <div className="relative mx-auto size-40">
            <HiArrowPath className="absolute size-40 text-gray-300" />
            <HiDevicePhoneMobile className="absolute m-10 size-20 text-gray-700" />
          </div>
          <div>Please rotate your device</div>
        </div>
      </div>

      <div className="relative">
        <div className="fixed left-2 top-2">
          <Link
            className="block size-20 text-white hover:text-gray-500"
            to={`/courts/${court.id}/tv`}
          >
            <HiVideoCamera className="size-5" />
          </Link>
        </div>
        <div className="fixed left-1/2">
          <Button
            color="white"
            className="block size-20 text-white hover:text-gray-500"
            onClick={() => setInverse()}
          >
            <HiArrowPath className="size-5" />
          </Button>
        </div>
        <div className="fixed right-2 top-2">
          <Link
            className="relative block size-20 text-white hover:text-gray-500"
            to={`/courts/${court.id}/control`}
          >
            <HiCpuChip className="absolute right-0 top-0 size-5 cursor-pointer" />
          </Link>
        </div>

        <div className="portrait:hidden">
          {court.active_match ? (
            SportSpecificDisplay && <SportSpecificDisplay court={court} />
          ) : (
            <div className="flex h-screen w-screen items-center justify-center">
              No active match
            </div>
          )}
        </div>
      </div>
    </>
  );
};
